import { zodResolver } from "@hookform/resolvers/zod";
import { Check, MapPin, Search } from "@tamagui/lucide-icons";
import {
  APIProvider,
  AdvancedMarker,
  Map as GoogleMap,
  useMap,
  useMapsLibrary,
} from "@vis.gl/react-google-maps";
import { useEffect, useRef, useState } from "react";
import { useController, useForm } from "react-hook-form";
import Svg, { Path, Text as SvgText } from "react-native-svg";
import { Circle, getTokenValue, useMedia, useTheme } from "tamagui";
import { z } from "zod";

import {
  useSearchPharmacies,
  useStartIdVerification,
  useUpsertUserProfile,
} from "@/api/generated";
import type { PharmacyLocation } from "@/api/generated/models/PharmacyLocation";
import { PharmacySearchMode } from "@/api/generated/models/PharmacySearchMode";
import { useGetUserProfile } from "@/api/queries";
import {
  Button,
  Footer,
  Header,
  Heading,
  Input,
  LoadingSpinner,
  Paragraph,
  ScrollView,
  Text,
  View,
  XStack,
  YStack,
} from "@/components";
import ErrorLabel from "@/components/forms/ErrorLabel";
import SegmentedControl from "@/components/forms/SegmentedControl";
import { useResolvedTheme } from "@/theme/helpers";
import {
  formatAddress,
  formatPhoneNumber,
  normalizeZipCode,
} from "@/utils/formatting";

const zipCodeSchema = z.string().regex(/^\d{5}$/, "Enter a 5-digit ZIP code");
const pharmacyNameSchema = z
  .string()
  .trim()
  .min(3, "Enter at least 3 characters");

const PHARMACY_MAP_ID = "433f41ac4a20f1f8d0c8fecd";

type PharmacySelectProps = {
  onClose?: () => void;
  onContinue?: () => void;
};

const PharmacySelect = ({ onClose, onContinue }: PharmacySelectProps) => {
  // Get actual color scheme in order to set map color scheme appropriately
  const resolvedTheme = useResolvedTheme();
  const { lg } = useMedia();
  const [mode, setMode] = useState<PharmacySearchMode>(
    PharmacySearchMode.retail,
  );
  const pharmacySelectSchema = z.object({
    zipCode: mode === PharmacySearchMode.retail ? zipCodeSchema : z.string(),
    pharmacyName:
      mode === PharmacySearchMode.mail_order ? pharmacyNameSchema : z.string(),
    selectedPharmacyId: z.string().min(1, "Please select a pharmacy"),
  });

  const { control, handleSubmit, setValue, trigger, clearErrors } = useForm<
    z.infer<typeof pharmacySelectSchema>
  >({
    resolver: zodResolver(pharmacySelectSchema),
    defaultValues: { zipCode: "", pharmacyName: "", selectedPharmacyId: "" },
  });

  const {
    field: zipCodeField,
    fieldState: { error: zipCodeError },
  } = useController({
    control,
    name: "zipCode",
  });

  const {
    field: pharmacyNameField,
    fieldState: { error: pharmacyNameError },
  } = useController({
    control,
    name: "pharmacyName",
  });

  const {
    field: selectedPharmacyField,
    fieldState: { error: selectedPharmacyError },
  } = useController({
    control,
    name: "selectedPharmacyId",
  });

  const selectedPharmacyId = selectedPharmacyField.value;

  const zipCode = zipCodeField.value;
  const pharmacyName = pharmacyNameField.value;

  const scrollViewRef = useRef<any>(null);
  const pharmacyPositions = useRef<Map<string, number>>(new Map());
  const shouldPrefillZip = useRef(true);
  const { data: userProfile, isLoading: isUserProfileLoading } =
    useGetUserProfile();
  const { mutate: upsertProfile, isPending: isSubmitting } =
    useUpsertUserProfile();
  const { mutate: startIdVerification } = useStartIdVerification();

  const retailSearch = useSearchPharmacies();
  const mailOrderSearch = useSearchPharmacies();
  const searchMutation =
    mode === PharmacySearchMode.retail ? retailSearch : mailOrderSearch;
  const { mutate: searchPharmacies, data: pharmaciesData = [] } =
    searchMutation;
  const isValidSearch =
    mode === PharmacySearchMode.retail
      ? zipCodeSchema.safeParse(zipCode).success
      : pharmacyNameSchema.safeParse(pharmacyName).success;

  const clearSelection = () => {
    pharmacyPositions.current.clear();
    selectedPharmacyField.onChange("");
    clearErrors("selectedPharmacyId");
  };

  const clearSearch = () => {
    searchMutation.reset();
    clearSelection();
  };

  useEffect(() => {
    if (
      mode === PharmacySearchMode.retail &&
      shouldPrefillZip.current &&
      userProfile?.zipCode
    ) {
      const normalizedZip = normalizeZipCode(userProfile.zipCode);
      shouldPrefillZip.current = false;
      setValue("zipCode", normalizedZip);
      if (zipCodeSchema.safeParse(normalizedZip).success) {
        searchPharmacies({ data: { mode: "retail", zip: normalizedZip } });
      }
    }
  }, [mode, searchPharmacies, setValue, userProfile?.zipCode]);

  const setSelectedPharmacyId = (id: string | null) => {
    selectedPharmacyField.onChange(id ?? "");
    if (id) {
      clearErrors("selectedPharmacyId");
    }
  };

  const onSubmit = async () => {
    if (searchMutation.isPending) return;
    const field =
      mode === PharmacySearchMode.retail ? "zipCode" : "pharmacyName";
    if (!(await trigger(field, { shouldFocus: true }))) return;
    clearSearch();
    searchPharmacies({
      data:
        mode === PharmacySearchMode.retail
          ? { mode, zip: zipCode }
          : { mode, name: pharmacyName.trim() },
    });
  };

  const onModeChange = (nextMode: PharmacySearchMode) => {
    if (nextMode === mode) return;
    setMode(nextMode);
    clearErrors(["zipCode", "pharmacyName"]);
    clearSelection();
  };

  const handleMapMarkerSelect = (id: string) => {
    setSelectedPharmacyId(id);
    const yPos = pharmacyPositions.current.get(id);
    if (yPos !== undefined && scrollViewRef.current) {
      scrollViewRef.current.scrollTo({ y: yPos, animated: true });
    }
  };

  const handleContinue = handleSubmit(({ selectedPharmacyId }) => {
    const selectedPharmacy = pharmaciesData.find(
      (pharmacy) => pharmacy.id === selectedPharmacyId,
    );
    if (!selectedPharmacy) return;

    if (!userProfile?.idVerification) {
      startIdVerification();
    }

    // if required fields are undefined we will let the api error
    upsertProfile(
      {
        data: {
          preferred_pharmacy: {
            id: selectedPharmacy.id,
            name: selectedPharmacy.name!,
            phone: selectedPharmacy.phone!,
            address: {
              line: selectedPharmacy.address?.line ?? [],
              city: selectedPharmacy.address?.city!,
              state: selectedPharmacy.address?.state!,
              postal_code: selectedPharmacy.address?.postalCode!,
            },
          },
        },
      },
      {
        onSuccess: () => {
          onContinue?.();
        },
        onError: () => {
          onClose?.();
        },
      },
    );
  });

  const hasPharmacyResults = pharmaciesData.length > 0;

  const mapTile = (
    <View
      width="100%"
      height="30vh"
      minHeight={200}
      maxHeight={400}
      $lg={{ flex: 1, height: "auto", maxHeight: "none" }}
      borderRadius="$100"
      overflow="hidden"
      borderWidth="$strokeWidth"
      borderColor="$strokeNeutral"
      backgroundColor="$fillNeutral"
      alignItems="center"
      justifyContent="center"
    >
      {hasPharmacyResults ? (
        <GoogleMap
          colorScheme={resolvedTheme === "dark" ? "DARK" : "LIGHT"}
          defaultZoom={11}
          mapId={PHARMACY_MAP_ID}
          reuseMaps={true}
          disableDefaultUI={true}
        >
          <MapMarkers
            pharmacies={pharmaciesData}
            selectedId={selectedPharmacyId}
            onSelect={handleMapMarkerSelect}
          />
        </GoogleMap>
      ) : (
        <MapPlaceholder />
      )}
    </View>
  );

  const zipInput = (
    <Input.Controlled
      ref={zipCodeField.ref}
      label="Zip code"
      placeholder="Enter 5-digit zip code"
      value={zipCode}
      inputMode="numeric"
      keyboardType="numeric"
      onChangeText={(text) => {
        shouldPrefillZip.current = false;
        zipCodeField.onChange(text);
        clearErrors("zipCode");
        clearSearch();
      }}
      onBlur={zipCodeField.onBlur}
      onSubmitEditing={onSubmit}
      maxLength={5}
      errorText={zipCodeError?.message}
      postfix={
        <Button.Wrapper
          padding="$75"
          round
          disabled={!isValidSearch || searchMutation.isPending}
          backgroundColor={
            !isValidSearch || searchMutation.isPending
              ? "$backgroundGround"
              : "$fillButtonPrimary"
          }
          onPress={onSubmit}
          aria-label="Search pharmacies"
        >
          {searchMutation.isPending ? (
            <LoadingSpinner size="$200" />
          ) : (
            <Search
              size="$200"
              color={
                !isValidSearch || searchMutation.isPending
                  ? "$textPrimary"
                  : "$iconCta"
              }
            />
          )}
        </Button.Wrapper>
      }
    />
  );

  const nameInput = (
    <Input.Controlled
      ref={pharmacyNameField.ref}
      label="Pharmacy name"
      placeholder="Enter pharmacy name"
      value={pharmacyName}
      onChangeText={(text) => {
        pharmacyNameField.onChange(text);
        clearErrors("pharmacyName");
        clearSearch();
      }}
      onBlur={pharmacyNameField.onBlur}
      onSubmitEditing={onSubmit}
      errorText={pharmacyNameError?.message}
      postfix={
        <Button.Wrapper
          padding="$75"
          round
          disabled={!isValidSearch || searchMutation.isPending}
          backgroundColor={
            !isValidSearch || searchMutation.isPending
              ? "$backgroundGround"
              : "$fillButtonPrimary"
          }
          onPress={onSubmit}
          aria-label="Search mail-order pharmacies"
        >
          {searchMutation.isPending ? (
            <LoadingSpinner size="$200" />
          ) : (
            <Search
              size="$200"
              color={
                !isValidSearch || searchMutation.isPending
                  ? "$textPrimary"
                  : "$iconCta"
              }
            />
          )}
        </Button.Wrapper>
      }
    />
  );

  const searchInput = mode === PharmacySearchMode.retail ? zipInput : nameInput;

  const pharmacyList = (
    <>
      {searchMutation.isError && (
        <ErrorLabel>
          Something went wrong searching pharmacies. Please try again.
        </ErrorLabel>
      )}

      {searchMutation.isSuccess && pharmaciesData.length === 0 && (
        <Text size="$sm" color="$textSecondary">
          {mode === PharmacySearchMode.retail
            ? "No pharmacies found for this ZIP code. Try a nearby ZIP code."
            : "No mail-order pharmacies found with that name. Try another name."}
        </Text>
      )}

      {pharmaciesData.length > 0 && (
        <YStack
          gap="$150"
          role="radiogroup"
          {...(selectedPharmacyError && {
            "aria-invalid": true,
            "aria-errormessage": "pharmacy-select-error",
            "aria-describedby": "pharmacy-select-error",
          })}
        >
          {pharmaciesData.map((pharmacy) => {
            const isSelected = selectedPharmacyId === pharmacy.id;
            return (
              <PharmacyCard
                key={pharmacy.id}
                pharmacy={pharmacy}
                selected={isSelected}
                isSponsored={pharmacy.is_sponsored}
                onSelect={() => setSelectedPharmacyId(pharmacy.id)}
                onLayout={(e) => {
                  pharmacyPositions.current.set(
                    pharmacy.id,
                    e.nativeEvent.layout.y,
                  );
                }}
              />
            );
          })}
        </YStack>
      )}
    </>
  );

  const searchModeToggle = (
    <SegmentedControl
      value={mode}
      onChange={onModeChange}
      dataTrackingId="pharmacy-search-mode"
      options={[
        { value: PharmacySearchMode.retail, label: "Local pharmacy" },
        { value: PharmacySearchMode.mail_order, label: "Mail order" },
      ]}
    />
  );

  return (
    <APIProvider
      apiKey={process.env.EXPO_PUBLIC_GOOGLE_MAPS_API_KEY ?? ""}
      disableUsageAttribution
    >
      <YStack flex={1}>
        <Header gap="$200" paddingHorizontal="$200">
          <Heading size="$lg">What is your preferred pharmacy?</Heading>
        </Header>
        {lg ? (
          <View
            flex={1}
            flexDirection="row"
            paddingTop="$200"
            paddingHorizontal="$200"
            gap="$200"
          >
            {mapTile}
            <YStack width="100%" gap="$200" flex={1}>
              {searchModeToggle}
              {searchInput}
              <ScrollView
                ref={scrollViewRef}
                flex={1}
                contentContainerStyle={{ gap: "$150", paddingBottom: "$200" }}
              >
                {pharmacyList}
              </ScrollView>
            </YStack>
          </View>
        ) : (
          <ScrollView
            ref={scrollViewRef}
            flex={1}
            contentContainerStyle={{
              gap: "$200",
              paddingHorizontal: "$200",
              paddingTop: "$200",
              paddingBottom: "$200",
            }}
          >
            {searchModeToggle}
            {mapTile}
            {searchInput}
            <YStack gap="$150">{pharmacyList}</YStack>
          </ScrollView>
        )}
        <YStack $lg={{ minHeight: "$900" }}>
          <Footer
            position="relative"
            paddingVertical="$200"
            paddingHorizontal="$200"
            $lg={{
              alignItems: "flex-end",
            }}
          >
            {selectedPharmacyError && (
              <ErrorLabel
                id="pharmacy-select-error"
                position="absolute"
                left="$200"
                right="$200"
                bottom="$700"
                $lg={{ left: "auto", width: "100%", maxWidth: "$3000" }}
                textProps={{ size: "$xs" }}
              >
                {selectedPharmacyError.message}
              </ErrorLabel>
            )}
            <Button
              constrained
              onPress={handleContinue}
              loading={isSubmitting || isUserProfileLoading}
              dataTrackingId="pharmacy-select-continue"
            >
              Continue
            </Button>
          </Footer>
        </YStack>
      </YStack>
    </APIProvider>
  );
};

const MapPlaceholder = () => (
  <YStack alignItems="center" justifyContent="center" gap="$100" padding="$300">
    <MapPin size="$300" color="$textSecondary" />
    <Text size="$sm" bold textAlign="center">
      Find your pharmacy
    </Text>
    <Text size="$sm" color="$textSecondary" textAlign="center">
      Search to see pharmacy locations on the map.
    </Text>
  </YStack>
);

const MapMarkers = ({
  pharmacies,
  selectedId,
  onSelect,
}: {
  pharmacies: PharmacyLocation[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}) => {
  const map = useMap();
  const core = useMapsLibrary("core");

  useEffect(() => {
    if (!map || !core || pharmacies.length === 0) return;

    const bounds = new core.LatLngBounds();
    pharmacies.forEach((p) => {
      if (p.position?.latitude && p.position?.longitude) {
        bounds.extend({
          lat: p.position.latitude,
          lng: p.position.longitude,
        });
      }
    });
    map.fitBounds(bounds, getTokenValue("$250"));
  }, [map, core, pharmacies]);

  return pharmacies.map((pharmacy) => {
    if (!pharmacy.position?.latitude || !pharmacy.position?.longitude)
      return null;

    const isSelected = selectedId === pharmacy.id;

    return (
      <AdvancedMarker
        key={pharmacy.id}
        position={{
          lat: pharmacy.position.latitude,
          lng: pharmacy.position.longitude,
        }}
        onClick={() => onSelect(pharmacy.id)}
      >
        <PharmacyPin selected={isSelected} />
      </AdvancedMarker>
    );
  });
};

const PharmacyPin = ({ selected }: { selected: boolean }) => {
  const theme = useTheme();
  const pinColor = selected ? theme.fillButtonPrimary.get() : theme.red.get();

  return (
    <Svg
      width={getTokenValue("$350")}
      height={getTokenValue("$350")}
      viewBox="0 0 80 100"
    >
      <Path
        d="M40 99.5 C-22.5 57.5 0 0 40 0.5 C80 0 102.5 57.5 40 99.5z"
        strokeWidth="1"
        stroke="grey"
        fill={pinColor}
      />
      <SvgText x="45%" y="50%" textAnchor="middle" fontSize="40px" fill="white">
        R
      </SvgText>
      <SvgText x="58%" y="62%" textAnchor="middle" fontSize="40px" fill="white">
        x
      </SvgText>
    </Svg>
  );
};

const SponsoredBanner = () => (
  <XStack
    gap="$100"
    alignItems="center"
    backgroundColor="$fillClinician"
    borderRadius="$100"
    padding="$100"
    marginBottom="$150"
  >
    <Circle backgroundColor="$fillButtonPrimary" size="$200">
      <Check size="$150" color="$textButtonPrimary" />
    </Circle>
    <Text size="$sm" fontWeight="400">
      Sponsored (Pharmacy Delivery within 1 hour Available)
    </Text>
  </XStack>
);

const PharmacyCard = ({
  pharmacy,
  selected,
  onSelect,
  onLayout,
  isSponsored,
}: {
  pharmacy: PharmacyLocation;
  selected: boolean;
  onSelect: () => void;
  onLayout?: (e: any) => void;
  isSponsored?: boolean;
}) => {
  const addressLines = formatAddress(pharmacy.address);

  return (
    <Button.Wrapper
      role="radio"
      aria-checked={selected}
      onPress={onSelect}
      onLayout={onLayout}
      padding="$200"
      borderRadius="$150"
      borderWidth="$strokeWidth"
      borderColor={selected ? "$fillButtonPrimary" : "$strokeNeutral"}
      backgroundColor="$fillInput"
      gap="$150"
      alignItems="flex-start"
      dataTrackingId="pharmacy-select-option"
    >
      <YStack width="100%" gap="$150">
        {isSponsored && <SponsoredBanner />}
        <XStack gap="$150" alignItems="center" width="100%">
          <RadioDot selected={selected} />
          <YStack gap="$50" flex={1}>
            <Text size="$md" fontWeight="700">
              {pharmacy.name || "Unnamed pharmacy"}
            </Text>
            {addressLines ? (
              <Paragraph size="$sm" color="$textSecondary">
                {addressLines}
              </Paragraph>
            ) : null}
            {pharmacy.phone ? (
              <Paragraph size="$sm" color="$textSecondary">
                {formatPhoneNumber(pharmacy.phone)}
              </Paragraph>
            ) : null}
          </YStack>
        </XStack>
      </YStack>
    </Button.Wrapper>
  );
};

const RadioDot = ({ selected }: { selected: boolean }) => (
  <Circle
    size="$250"
    borderWidth="$strokeWidth"
    borderColor={selected ? "$fillButtonPrimary" : "$strokeNeutral"}
    alignItems="center"
    justifyContent="center"
  >
    {selected ? (
      <Circle size="$125" backgroundColor="$fillButtonPrimary" />
    ) : null}
  </Circle>
);

export default PharmacySelect;
